/**
 * AiAssistantPanel — floating AI chat panel for the N-400 wizard.
 *
 * Features:
 * • Floating "Ask AI" button (bottom-right on desktop, bottom-right on mobile)
 * • Desktop: 420px slide-in from right
 * • Mobile: full-width bottom sheet (85dvh, slides up — dvh avoids iOS keyboard overlap)
 * • Step-aware quick-question chips with instant static answers
 * • Streaming SSE responses (delta events)
 * • Graceful degradation when AI is unavailable (static-only mode)
 * • "open-ai-panel" custom DOM event from FieldHelpButton
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { chat, type ChatMessageItem } from "@/lib/endpoints";
import { STEP_CHIPS, DEFAULT_CHIPS, type ChipDef } from "@/lib/ai-chips";
import { Markdown } from "@/components/Markdown";

interface Props {
  caseId: string;
  wizardStep?: string;
}

type PanelState = "idle" | "loading" | "ready" | "streaming" | "unavailable";

export function AiAssistantPanel({ caseId, wizardStep }: Props) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [streamingText, setStreamingText] = useState("");
  const [panelState, setPanelState] = useState<PanelState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();

  // Refs for values needed inside the stable event listener (avoids stale closure).
  // Updated via effects whenever the underlying value changes.
  const aiAvailableRef = useRef(true);
  const wizardStepRef = useRef(wizardStep);
  // Carries the fieldKey from a FieldHelpButton click into the next handleSend call.
  const pendingFieldKeyRef = useRef<string | null>(null);

  // Detect mobile width.
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Load chat history + availability when panel opens.
  const historyQ = useQuery({
    queryKey: ["chat", caseId],
    queryFn: () => chat.history(caseId),
    enabled: open,
    staleTime: 0,
    retry: false,
  });

  const aiAvailable = historyQ.data?.available ?? true; // optimistic until loaded
  const messages: ChatMessageItem[] = historyQ.data?.items ?? [];
  const chips: ChipDef[] = (wizardStep && STEP_CHIPS[wizardStep]) ? STEP_CHIPS[wizardStep] : DEFAULT_CHIPS;

  // Keep refs in sync for the event handler.
  useEffect(() => { aiAvailableRef.current = aiAvailable; }, [aiAvailable]);
  useEffect(() => { wizardStepRef.current = wizardStep; }, [wizardStep]);

  // Derive panel display state.
  const displayState: PanelState = panelState === "streaming"
    ? "streaming"
    : historyQ.isLoading
      ? "loading"
      : !aiAvailable
        ? "unavailable"
        : "ready";

  // Auto-scroll to bottom.
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText, open]);

  // Focus input on open.
  useEffect(() => {
    if (open && displayState !== "unavailable") {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open, displayState]);

  // Listen for FieldHelpButton events.
  // Uses refs (not closures) for aiAvailable / wizardStep to avoid stale values.
  useEffect(() => {
    function handle(e: Event) {
      const { question, fieldKey } = (e as CustomEvent<{ question: string; fieldKey?: string }>).detail;
      setOpen(true);
      // Store the field key so the next handleSend can pass it to the API.
      pendingFieldKeyRef.current = fieldKey ?? null;
      setInput(question ?? "");

      if (!aiAvailableRef.current) {
        // Input is hidden in unavailable mode — inject a static answer immediately
        // so the question is not silently dropped.
        const step = wizardStepRef.current;
        const allChips = (step && STEP_CHIPS[step]) ? STEP_CHIPS[step] : DEFAULT_CHIPS;
        const match = allChips.find((c) => c.question === question);
        const replyContent = match?.staticAnswer ??
          "For this question, please refer to the official USCIS N-400 instructions at uscis.gov/n-400, or consult a licensed immigration attorney for guidance specific to your situation.";
        const ts = Date.now();
        queryClient.setQueryData<{ items: ChatMessageItem[]; total: number; available: boolean }>(
          ["chat", caseId],
          (prev) => ({
            items: [
              ...(prev?.items ?? []),
              { id: `fhb-user-${ts}`, role: "user" as const, content: question, created_at: new Date().toISOString() },
              { id: `fhb-ai-${ts}`, role: "assistant" as const, content: replyContent, created_at: new Date().toISOString() },
            ],
            total: (prev?.total ?? 0) + 2,
            available: false,
          }),
        );
      } else {
        setTimeout(() => inputRef.current?.focus(), 200);
      }
    }
    window.addEventListener("open-ai-panel", handle);
    return () => window.removeEventListener("open-ai-panel", handle);
  }, [caseId, queryClient]); // refs handle aiAvailable + wizardStep; caseId/queryClient are stable

  async function handleSend(overrideMsg?: string) {
    const msg = (overrideMsg ?? input).trim();
    if (!msg || panelState === "streaming") return;

    setInput("");
    setError(null);
    setPanelState("streaming");
    setStreamingText("");

    // Optimistic user message.
    queryClient.setQueryData<{ items: ChatMessageItem[]; total: number; available: boolean }>(
      ["chat", caseId],
      (prev) => ({
        items: [
          ...(prev?.items ?? []),
          {
            id: `opt-${Date.now()}`,
            role: "user" as const,
            content: msg,
            created_at: new Date().toISOString(),
          },
        ],
        total: (prev?.total ?? 0) + 1,
        available: prev?.available ?? true,
      }),
    );

    try {
      // Consume the pending field key (set by FieldHelpButton event handler) and
      // pass it to the API so FieldContext prompt injection activates.
      const fieldKey = pendingFieldKeyRef.current ?? undefined;
      pendingFieldKeyRef.current = null;
      const reader = await chat.sendStream(caseId, msg, wizardStep, fieldKey);
      const decoder = new TextDecoder();
      let accumulated = "";
      let buf = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (!data || data.includes('"message_id"')) continue;
          try {
            const parsed = JSON.parse(data) as { text?: string; error?: string };
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.text) {
              accumulated += parsed.text;
              setStreamingText(accumulated);
            }
          } catch (parseErr) {
            if (parseErr instanceof Error && parseErr.message !== "Unexpected end of JSON input") {
              throw parseErr;
            }
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setPanelState("ready");
      setStreamingText("");
      queryClient.invalidateQueries({ queryKey: ["chat", caseId] });
    }
  }

  function handleChipClick(chip: ChipDef) {
    if (aiAvailable) {
      // Show static answer immediately for instant feedback, then stream real answer.
      setInput(chip.question);
      handleSend(chip.question);
    } else {
      // Static-only mode: inline answer without API call.
      const staticMsg: ChatMessageItem = {
        id: `static-user-${Date.now()}`,
        role: "user",
        content: chip.question,
        created_at: new Date().toISOString(),
      };
      const staticReply: ChatMessageItem = {
        id: `static-ai-${Date.now()}`,
        role: "assistant",
        content: chip.staticAnswer,
        created_at: new Date().toISOString(),
      };
      queryClient.setQueryData<{ items: ChatMessageItem[]; total: number; available: boolean }>(
        ["chat", caseId],
        (prev) => ({
          items: [...(prev?.items ?? []), staticMsg, staticReply],
          total: (prev?.total ?? 0) + 2,
          available: false,
        }),
      );
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  async function handleClear() {
    if (!confirm("Clear conversation history?")) return;
    await chat.clear(caseId);
    queryClient.setQueryData(["chat", caseId], { items: [], total: 0, available: aiAvailable });
  }

  const isStreaming = panelState === "streaming";

  // Panel geometry — desktop: right slide-in; mobile: bottom sheet.
  const panelClasses = cn(
    "fixed z-50 flex flex-col bg-white shadow-2xl border-slate-200 transition-transform duration-300 ease-out",
    isMobile
      ? "inset-x-0 bottom-0 h-[85dvh] rounded-t-2xl border-t"
      : "inset-y-0 right-0 w-full sm:w-[420px] border-l",
    open
      ? "translate-y-0 translate-x-0"
      : isMobile
        ? "translate-y-full"
        : "translate-x-full",
  );

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close AI assistant" : "Open AI assistant"}
        className={cn(
          "fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold shadow-lg transition-all duration-200",
          open
            ? "bg-slate-800 text-white"
            : "bg-blue-600 text-white hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-xl",
        )}
      >
        {open ? (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="hidden sm:inline">Close</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
            </svg>
            Ask AI
          </>
        )}
      </button>

      {/* Slide-in / bottom-sheet panel */}
      <div className={panelClasses} aria-hidden={!open}>

        {/* Header */}
        <div className={cn(
          "flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0",
          isMobile && "pt-3 pb-3",
        )}>
          {/* Mobile drag handle */}
          {isMobile && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-slate-200 rounded-full" />
          )}
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900 leading-tight">N-400 Assistant</p>
              <p className="text-[10px] text-slate-400">
                {aiAvailable ? "AI-powered · not legal advice" : "Quick answers · not legal advice"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {messages.length > 0 && (
              <button
                onClick={handleClear}
                className="text-xs text-slate-400 hover:text-red-500 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
              >
                Clear
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 shrink-0">
          <p className="text-[11px] text-amber-700 leading-snug">
            <strong>Not legal advice.</strong> For complex situations, consult a licensed immigration attorney.
          </p>
        </div>

        {/* Unavailable state banner */}
        {displayState === "unavailable" && (
          <div className="mx-4 mt-3 rounded-xl bg-slate-50 border border-slate-200 px-3.5 py-2.5 shrink-0">
            <p className="text-xs text-slate-500 leading-snug">
              Live AI responses are not available in this environment. Click a question below for a pre-written answer.
            </p>
          </div>
        )}

        {/* Message list */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {displayState === "loading" && (
            <div className="flex justify-center py-8">
              <div className="w-5 h-5 border-2 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
            </div>
          )}

          {/* Empty state with step-aware chips */}
          {(displayState === "ready" || displayState === "unavailable") && messages.length === 0 && !isStreaming && (
            <EmptyState chips={chips} onChipClick={handleChipClick} />
          )}

          {/* Chips above messages when history exists */}
          {(displayState === "ready" || displayState === "unavailable") && messages.length > 0 && !isStreaming && (
            <div className="flex flex-wrap gap-1.5 pb-1">
              {chips.slice(0, 2).map((c) => (
                <button
                  key={c.question}
                  onClick={() => handleChipClick(c)}
                  disabled={isStreaming}
                  className="text-xs text-blue-600 bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-full transition-colors disabled:opacity-50"
                >
                  {c.question}
                </button>
              ))}
            </div>
          )}

          {/* Messages */}
          {messages.map((msg) => (
            <MessageBubble key={msg.id} msg={msg} />
          ))}

          {/* Streaming bubble */}
          {isStreaming && (
            <StreamingBubble text={streamingText} />
          )}

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-3.5 py-2.5 text-xs text-red-600">
              {error}
              <button
                onClick={() => { setError(null); handleSend(messages[messages.length - 1]?.role === "user" ? messages[messages.length - 1].content : undefined); }}
                className="ml-2 underline font-medium"
              >
                Retry
              </button>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input — hidden in unavailable mode */}
        {displayState !== "unavailable" && (
          <div className="shrink-0 border-t border-slate-100 px-4 py-3">
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isStreaming}
                placeholder="Ask a question about your N-400…"
                rows={isMobile ? 1 : 2}
                className={cn(
                  "flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm placeholder:text-slate-400",
                  "focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400",
                  "disabled:opacity-50 disabled:bg-slate-50 transition-colors",
                )}
              />
              <button
                onClick={() => handleSend()}
                disabled={isStreaming || !input.trim()}
                className={cn(
                  "shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all",
                  "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed",
                )}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            {!isMobile && (
              <p className="text-[10px] text-slate-400 mt-1.5">Enter to send · Shift+Enter for new line</p>
            )}
          </div>
        )}
      </div>

      {/* Backdrop */}
      {open && (
        <div
          className={cn(
            "fixed inset-0 z-40 bg-slate-900/30 transition-opacity",
            isMobile ? "block" : "hidden sm:block sm:bg-transparent sm:pointer-events-none",
          )}
          onClick={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function EmptyState({ chips, onChipClick }: { chips: ChipDef[]; onChipClick: (c: ChipDef) => void }) {
  return (
    <div className="text-center py-6">
      <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-3">
        <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
        </svg>
      </div>
      <p className="text-sm font-semibold text-slate-700 mb-1">Ask me anything about N-400</p>
      <p className="text-xs text-slate-400 mb-4 max-w-xs mx-auto">
        Common questions for this step:
      </p>
      <div className="flex flex-col gap-2 text-left">
        {chips.map((c) => (
          <button
            key={c.question}
            onClick={() => onChipClick(c)}
            className="text-sm text-left text-blue-700 bg-blue-50 hover:bg-blue-100 px-3.5 py-2.5 rounded-xl transition-colors border border-blue-100 leading-snug"
          >
            {c.question}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg }: { msg: ChatMessageItem }) {
  const isUser = msg.role === "user";
  return (
    <div className={cn("flex gap-2.5", isUser ? "flex-row-reverse" : "flex-row")}>
      {!isUser && (
        <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0 mt-0.5">
          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
          </svg>
        </div>
      )}
      <div className={cn(
        "max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed break-words",
        isUser
          ? "bg-blue-600 text-white rounded-tr-sm whitespace-pre-wrap"
          : "bg-slate-50 border border-slate-200 text-slate-700 rounded-tl-sm",
      )}>
        {isUser ? msg.content : <Markdown>{msg.content}</Markdown>}
      </div>
    </div>
  );
}

function StreamingBubble({ text }: { text: string }) {
  return (
    <div className="flex gap-2.5">
      <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0 mt-0.5">
        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
        </svg>
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-2xl rounded-tl-sm px-3.5 py-2.5 max-w-[85%]">
        {text ? (
          <div className="text-sm text-slate-700 leading-relaxed break-words">
            <Markdown>{text}</Markdown>
            <span className="inline-block w-0.5 h-4 bg-blue-500 ml-0.5 animate-pulse align-text-bottom" />
          </div>
        ) : (
          <div className="flex gap-1 items-center py-1">
            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0ms]" />
            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:150ms]" />
            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:300ms]" />
          </div>
        )}
      </div>
    </div>
  );
}
